import { motion } from 'framer-motion';
import { Bell, X, Heart, Sparkles, Eye, MessageCircleHeart } from 'lucide-react';
import type { PushPromptVariant } from '@/lib/pushPromptAnalytics';
import { blurredPhotoFilter } from '@/lib/designTokens';

interface BehavioralPushModalProps {
  variant: PushPromptVariant;
  title: string;
  subtitle: string;
  body?: string;
  primaryCta: string;
  secondaryCta?: string;
  loading?: boolean;
  blurredPhotoUrl?: string | null;
  onAccept: () => void;
  onDecline: () => void;
}

function VariantIcon({ variant }: { variant: PushPromptVariant }) {
  if (variant === 'A_likes_blur') return <Heart size={26} color="#e11d48" fill="#e11d48" />;
  if (variant === 'B_first_match') return <Sparkles size={26} color="#fbbf24" />;
  if (variant === 'C_exit_intent') return <Eye size={26} color="#e11d48" />;
  if (variant === 'E_first_message') return <MessageCircleHeart size={26} color="#e11d48" />;
  return <Bell size={26} color="#e11d48" />;
}

export function BehavioralPushModal({
  variant,
  title,
  subtitle,
  body,
  primaryCta,
  secondaryCta = 'Not now',
  loading,
  blurredPhotoUrl,
  onAccept,
  onDecline,
}: BehavioralPushModalProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onDecline();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.78)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
      }}
      data-testid={`push-modal-${variant}`}
    >
      <motion.div
        initial={{ y: 30, opacity: 0, scale: 0.96 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 30, opacity: 0, scale: 0.96 }}
        transition={{ type: 'spring', damping: 26, stiffness: 320 }}
        style={{
          background: 'linear-gradient(180deg, #1a0a14 0%, #0d0508 100%)',
          border: '1px solid rgba(225,29,72,0.4)',
          borderRadius: 22,
          padding: '24px 22px 20px',
          maxWidth: 420,
          width: '100%',
          position: 'relative',
          boxShadow: '0 20px 60px rgba(225,29,72,0.3)',
          textAlign: 'center',
        }}
      >
        <button
          onClick={onDecline}
          aria-label="Dismiss"
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            background: 'rgba(255,255,255,0.06)',
            border: 'none',
            borderRadius: '50%',
            width: 30,
            height: 30,
            color: 'rgba(255,255,255,0.6)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <X size={16} />
        </button>

        {variant === 'A_likes_blur' && blurredPhotoUrl ? (
          <div
            style={{
              width: 110,
              height: 110,
              borderRadius: '50%',
              margin: '4px auto 16px',
              backgroundImage: `url(${blurredPhotoUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: blurredPhotoFilter,
              border: '3px solid rgba(225,29,72,0.6)',
              boxShadow: '0 10px 30px rgba(225,29,72,0.35)',
            }}
            aria-hidden
          />
        ) : (
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: 'rgba(225,29,72,0.18)',
              border: '1px solid rgba(225,29,72,0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '4px auto 16px',
            }}
          >
            <VariantIcon variant={variant} />
          </div>
        )}

        <h2 style={{ color: 'white', fontSize: 22, fontWeight: 800, margin: '0 0 8px', lineHeight: 1.25 }}>
          {title}
        </h2>
        {subtitle && (
          <p style={{ color: 'rgba(255,255,255,0.78)', fontSize: 15, lineHeight: 1.5, margin: '0 0 6px' }}>
            {subtitle}
          </p>
        )}
        {body && (
          <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, lineHeight: 1.5, margin: '0 0 20px' }}>
            {body}
          </p>
        )}

        <button
          onClick={onAccept}
          disabled={loading}
          data-testid={`push-modal-${variant}-accept`}
          style={{
            width: '100%',
            background: '#e11d48',
            color: 'white',
            border: 'none',
            borderRadius: 12,
            padding: '14px',
            fontSize: 16,
            fontWeight: 700,
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1,
            marginTop: body ? 4 : 18,
          }}
        >
          {loading ? 'Enabling…' : primaryCta}
        </button>
        <button
          onClick={onDecline}
          data-testid={`push-modal-${variant}-decline`}
          style={{
            width: '100%',
            background: 'transparent',
            color: 'rgba(255,255,255,0.5)',
            border: 'none',
            padding: '12px',
            fontSize: 13,
            fontWeight: 500,
            cursor: 'pointer',
            marginTop: 4,
          }}
        >
          {secondaryCta}
        </button>
      </motion.div>
    </motion.div>
  );
}
